/* eslint-disable indent */
import { FC, useContext, useEffect, useState } from "react";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { toast } from "react-toastify";
import { captureException } from "@sentry/react";
import { Loader2 } from "lucide-react";
import { GalleryImage } from "@/components/atoms/GalleryImage";
import { AddAudioModal } from "@/components/molecules/AddAudioModal";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import CustomJoyride from "components/atoms/Joyride";
import ActionFolderModal from "components/molecules/ActionFolderModal";
import { ADetailerModal } from "components/molecules/ADetailerModal";
import { BGRemovalModal } from "components/molecules/BGRemovalModal";
import CanvasModal from "components/molecules/CanvasModal";
import { DeleteAllConfirmationModal } from "components/molecules/DeleteAllModal";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import EligibleCompetitionsModal from "components/molecules/EligibleCompetitionsModal";
import { FaceSwapModal } from "components/molecules/FaceSwapModal";
import { InpaintingModal } from "components/molecules/InpaintingModal";
import ShareModal from "components/molecules/ShareModal";
import { UpscalerModal } from "components/molecules/UpscalerModal";
import { useTutorial } from "components/organisms/TutorialProvider";
import { addImagesToFolder, deleteAllImages, deleteImage, onToggleVisibility } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, IDeleteImageRequest, ImageDTO, UserPremiumType } from "types";
import { imageViewerTutorialSteps } from "utils/joyride/imageViewerTutorialSteps";
import * as Styled from "./ImageViewer.styled";
import Masonry from "react-masonry-css";
type ImageViewerProps = {
  imageDtos: ImageDTO[];
  onImageClick: (image: ImageDTO) => void;
  updateImage: (image: ImageDTO) => void;
  onSubmitEdit: (editRequest: string, isPremium: boolean, imageId: string, editType: string, goldCost: number, negativePrompt?: string, cfgScale?: number, numDetections?: number, urlOrFile?: string | File, base64Image?: string, creativity?: number, model?: string) => void;
  onVote: (imageId: string) => void;
};
export const ImageViewer: FC<ImageViewerProps> = ({
  imageDtos,
  onImageClick,
  onSubmitEdit,
  onVote
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [images, setImages] = useState<ImageDTO[]>(imageDtos);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editImageId, setEditImageId] = useState<string | null>(null); // Track the image being edited
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [selectedImage, setSelectedImage] = useState<ImageDTO | null>(null);
  const [deletedImageIds, setDeletedImageIds] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showDeleteAllConfirmation, setShowDeleteAllConfirmation] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showEligibleCompetitionsModal, setShowEligibleCompetitionsModal] = useState(false);
  const [isADetailerModalOpen, setIsADetailerModalOpen] = useState(false);
  const [isFaceSwapModalOpen, setIsFaceSwapModalOpen] = useState(false);
  const [isBGRemovalModalOpen, setIsBGRemovalModalOpen] = useState(false);
  const [isCanvasModalOpen, setCanvasModalOpen] = useState<boolean>(false);
  const [isInpaintingModalOpen, setInpaintingModalOpen] = useState(false);
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const [editImage, setEditImage] = useState<ImageDTO | null>(null);
  const [isUpscalerModalOpen, setUpscalerModalOpen] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const handleCloseAudioModal = () => {
    setIsAudioModalOpen(false);
  };
  const handleAudioSubmit = () => {
    setIsAudioModalOpen(false);
    setAudioLoading(true);
  };
  const {
    tutorialState,
    tutorialStateInitialized,
    startTutorial,
    completeTutorial
  } = useTutorial();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    if (!tutorialStateInitialized || !tutorialState) return;
    if (tutorialState.generateImageForm.completed && !tutorialState.imageViewer.completed && !tutorialState.imageViewer.started && images.length > 0) {
      startTutorial("imageViewer");
    }
  }, [tutorialState, startTutorial, images.length, tutorialStateInitialized]);
  useEffect(() => {
    if (tutorialState?.imageViewer.started && !tutorialState.imageViewer.completed) {
      setRun(true);
    }
  }, [tutorialState]);
  const handleJoyrideCallback = (data: {
    status: any;
    type: any;
    action: any;
    index: any;
  }) => {
    const {
      status,
      type,
      action
    } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) {
      completeTutorial("imageViewer");
      setRun(false);
      setStepIndex(0);
    } else if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        setStepIndex(prev => prev + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(prev => prev - 1);
      }
    }
  };
  const handleCloseUpscalerModal = () => {
    setUpscalerModalOpen(false);
  };
  const openAddModal = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    setShowMoveModal(true);
  };
  const handleSetSelectedFolderId = (folderId: string | null) => {
    setTargetFolderId(folderId);
  };
  const handleAddImagesToFolder = async (folderId: string) => {
    const result = await addImagesToFolder(folderId, Array.from(selectedImages));
    if (result.success) {
      handleDeselectAll();
      setShowMoveModal(false);
    }
  };
  const handleUpscalerModalClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      const editedImage = images.find(image => image.id === imageId);
      if (editedImage && editedImage.imageMetadata.npcFieldsMap["Edit Type"] === "UPSCALER") {
        toast.error("This image has already been upscaled and cannot be upscaled again.");
      } else if (imageId) {
        setEditImageId(imageId);
        setUpscalerModalOpen(true);
      }
    }
  };
  const handleInpaintClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      const editedImage = images.find(image => image.id === imageId);
      if (editedImage) {
        setEditImage(editedImage);
      }
      setEditImageId(imageId);
      setCanvasModalOpen(true);
    }
  };
  const handleCloseCanvasModal = () => {
    setCanvasModalOpen(false);
  };
  const handleMaskSubmit = (maskData: string | null) => {
    setBase64Image(maskData || "");
    setCanvasModalOpen(false);
    setInpaintingModalOpen(true);
  };
  const handleCloseInpaintingModal = () => {
    setInpaintingModalOpen(false);
    setBase64Image(null); // Clear the image data
  };
  const handleCloseBGRemovalModal = () => {
    setIsBGRemovalModalOpen(false);
  };
  const handleBGRemovalClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setIsBGRemovalModalOpen(true);
    }
  };
  const handleShareClick = (e: React.MouseEvent, imageId?: string) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedImage = images.find(image => image.id === imageId);
    if (selectedImage) {
      setSelectedImage(selectedImage);
      setIsShareModalOpen(true);
    }
  };
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };
  const [currentImageId, setCurrentImageId] = useState<string | null>(null);
  const handleCompetitionsClick = (imageId: string) => {
    setCurrentImageId(imageId);
    setShowEligibleCompetitionsModal(true);
  };
  const handleCloseEligibleCompetitionsModal = () => {
    setShowEligibleCompetitionsModal(false);
    setCurrentImageId(null);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const handleSelectImage = (imageId: string) => {
    setIsSelectionMode(true); // Automatically enable selection mode
    setSelectedImages(prev => {
      const newSelectedImages = new Set(prev);
      if (newSelectedImages.has(imageId)) {
        newSelectedImages.delete(imageId);
      } else {
        newSelectedImages.add(imageId);
      }
      return newSelectedImages;
    });
  };
  const handleDeselectAll = () => {
    setIsSelectionMode(false);
    setSelectedImages(new Set());
  };
  const handleDeleteAll = async () => {
    try {
      const dataPayload: IDeleteImageRequest = {
        imageIds: Array.from(selectedImages)
      };
      await deleteAllImages(dataPayload);
      setDeletedImageIds(prev => {
        const newDeletedImageIds = new Set(prev);
        selectedImages.forEach(id => newDeletedImageIds.add(id));
        return newDeletedImageIds;
      });
      setImages(prevImages => prevImages.filter(image => !selectedImages.has(image.id)));
      setShowDeleteAllConfirmation(false);
      handleDeselectAll();
      dispatch({
        type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
        payload: {
          ...state,
          isOverlayVisible: false
        }
      });
    } catch (error: any) {
      captureException("Failed to delete images", error);
      setShowDeleteAllConfirmation(false);
      handleDeselectAll();
    }
  };
  const handleEditSubmit = (editRequest: string, isPremium: boolean, editType: string, goldCost: number, negativePrompt?: string, cfgScale?: number, numDetections?: number, urlOrFile?: string | File, base64Image?: string, creativity?: number, model?: string) => {
    if (editImageId) {
      onSubmitEdit(editRequest, isPremium, editImageId, editType, goldCost, negativePrompt, cfgScale, numDetections, urlOrFile, base64Image, creativity, model);
    }
    setShowEditModal(false);
  };
  const handleCloseFaceSwapModal = () => {
    setIsFaceSwapModalOpen(false);
  };
  const handleFaceSwapClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setIsFaceSwapModalOpen(true);
    }
  };
  const handleADetailerClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setIsADetailerModalOpen(true);
    }
  };
  const handleCloseADetailerModal = () => {
    setIsADetailerModalOpen(false);
  };
  const handleEditCancel = () => {
    setShowEditModal(false);
  };
  const handleEditClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setShowEditModal(true);
    }
  };
  const handleAudioClick = (imageId?: string) => {
    if (imageId) {
      setEditImageId(imageId);
      setSelectedImage(images.find(image => image.id === imageId) || null);
      setIsAudioModalOpen(true);
    }
  };
  useEffect(() => {
    if (imageDtos) {
      const mergedImages = imageDtos.filter(dto => !deletedImageIds.has(dto.id)).map(dto => ({
        ...dto,
        isVotedByUser: images.find(img => img.id === dto.id)?.votedByUser ?? dto.votedByUser,
        voteCount: images.find(img => img.id === dto.id)?.voteCount ?? dto.voteCount
      }));
      setImages(mergedImages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageDtos]);
  const handleVote = async (imageId: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    const currentImage = images.find(img => img.id === imageId);
    if (!currentImage) return;
    const newVoteStatus = !currentImage.votedByUser;
    try {
      await onVote(imageId);
      setImages(prevImages => prevImages.map(img => img.id === imageId ? {
        ...img,
        votedByUser: newVoteStatus,
        voteCount: img.voteCount + (newVoteStatus ? 1 : -1)
      } : img));
      imageDtos.find(img => img.id === imageId)!.votedByUser = newVoteStatus;
      imageDtos.find(img => img.id === imageId)!.voteCount = currentImage.voteCount;
    } catch (error: any) {
      captureException("Failed to update vote status.", error);
    }
  };
  const prepareForDelete = (imageId: string) => {
    setImageToDelete(imageId);
    setShowDeleteConfirmation(true);
  };
  const handleConfirmDelete = async () => {
    if (!imageToDelete) return;
    try {
      await deleteImage(imageToDelete);
      setDeletedImageIds(prev => new Set(prev).add(imageToDelete));
      setImages(prevImages => prevImages.filter(img => img.id !== imageToDelete));
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
      dispatch({
        type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
        payload: {
          ...state,
          isOverlayVisible: false
        }
      });
    } catch (error: any) {
      captureException("Failed to delete image", error);
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setImageToDelete(null);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const isUserImage = (imageUsername: string) => state.username === imageUsername;
  const [toggles, setToggles] = useState<{
    [key: string]: boolean;
  }>(imageDtos.reduce((acc, image) => ({
    ...acc,
    [image.id]: image.public
  }), {}));
  useEffect(() => {
    setToggles(imageDtos.reduce((acc, image) => ({
      ...acc,
      [image.id]: image.public
    }), {}));
  }, [imageDtos]);
  const ImageContainer = Styled.ImageContainer;
  useEffect(() => {
    const userImages = images.filter(img => img.imageMetadata.username === state.username);
    const newToggles = userImages.reduce((acc, image) => ({
      ...acc,
      [image.id]: image.public
    }), {});
    setToggles(newToggles);
    const updatedImages = images.map(img => {
      const userImg = userImages.find(uImg => uImg.id === img.id);
      return userImg ? {
        ...img,
        public: userImg.public,
        voteCount: userImg.voteCount,
        votedByUser: userImg.votedByUser
      } : img;
    });
    setImages(updatedImages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.username]);

  // useEffect(() => {
  //     setImages(imageDtos);
  // }, [imageDtos]);

  const handleToggle = (imageId: string) => {
    const currentVisibility = toggles[imageId];
    const newVisibility = !currentVisibility;
    onToggleVisibility(imageId, newVisibility).then(() => {
      setToggles(prev => ({
        ...prev,
        [imageId]: newVisibility
      }));
      setImages(prevImages => prevImages.map(img => img.id === imageId ? {
        ...img,
        public: newVisibility
      } : img));
      imageDtos.find(img => img.id === imageId)!.public = newVisibility;
    }).catch(error => {
      captureException("Failed to update visibility status.", error);
    });
  };
  const breakpointColumnsObj = {
    default: Math.min(images.length, 2),
    1100: Math.min(images.length, 2),
    700: Math.min(images.length, 1),
    500: Math.min(images.length, 1)
  };
  const renderImage = (image: ImageDTO) => <GalleryImage key={image.id} item={image} isUserImage={isUserImage} onImageClick={onImageClick} handleToggle={handleToggle} handleEditClick={handleEditClick} handleADetailerClick={handleADetailerClick} handleFaceSwapClick={handleFaceSwapClick} handleBGRemovalClick={handleBGRemovalClick} handleInpaintClick={handleInpaintClick} handleUpscaleClick={handleUpscalerModalClick} handleAudioClick={handleAudioClick} prepareForDelete={prepareForDelete} handleVote={handleVote} isSelectionMode={isSelectionMode} handleSelectImage={handleSelectImage} isSelected={selectedImages.has(image.id)} userGallery={true} handleShareClick={handleShareClick} handleCompetitionsClick={() => handleCompetitionsClick(image.id)} showDeleteConfirmation={showDeleteConfirmation} priority={true} data-sentry-element="GalleryImage" data-sentry-component="renderImage" data-sentry-source-file="index.tsx" />;
  return <>
            <CustomJoyride steps={imageViewerTutorialSteps} run={run} stepIndex={stepIndex} disableScrolling={false} callback={handleJoyrideCallback} data-sentry-element="CustomJoyride" data-sentry-source-file="index.tsx" />
            {!!images.length && <ImageContainer>
                    <Masonry breakpointCols={breakpointColumnsObj} className="flex w-full -ml-4" columnClassName="pl-4 bg-clip-padding">
                        {images.map(renderImage)}
                    </Masonry>
                    {!isSelectionMode && images.length > 0 && <Styled.SingleButtonContainer>
                            <Button variant={ButtonType.PRIMARY} onClick={() => setIsSelectionMode(true)}>
                                Enable Selection Mode
                            </Button>
                        </Styled.SingleButtonContainer>}
                    {isSelectionMode && <Styled.ButtonContainer>
                            <Button variant={ButtonType.PRIMARY} onClick={() => openAddModal()} disabled={selectedImages.size === 0}>
                                Add Selected to Folder
                            </Button>
                            <Button variant={ButtonType.PRIMARY} onClick={() => setShowDeleteAllConfirmation(true)}>
                                Delete Selected Images
                            </Button>
                            <Button variant={ButtonType.ERROR} onClick={handleDeselectAll}>
                                Cancel
                            </Button>
                        </Styled.ButtonContainer>}
                    <DeleteAllConfirmationModal isOpen={showDeleteAllConfirmation} onConfirm={handleDeleteAll} onCancel={() => setShowDeleteAllConfirmation(false)} deleteCount={selectedImages.size} />
                    <DeleteConfirmationModal isOpen={showDeleteConfirmation} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} text="image" />
                    <ActionFolderModal open={showMoveModal} onClose={() => setShowMoveModal(false)} onAction={handleAddImagesToFolder} selectedFolderId={targetFolderId} setSelectedFolderId={handleSetSelectedFolderId} text={"Add Images to Folder"} />
                    {editImageId && editImage && <InpaintingModal isOpen={showEditModal} onCancel={handleEditCancel} base64Image={base64Image!} originalImage={editImage.image} imageId={editImageId} handleInpaintClick={handleInpaintClick} onSubmit={handleEditSubmit} />}
                    <ShareModal isOpen={isShareModalOpen} image={selectedImage} onClose={handleCloseShareModal} />
                    {currentImageId && <EligibleCompetitionsModal isOpen={showEligibleCompetitionsModal} onCancel={handleCloseEligibleCompetitionsModal} imageId={currentImageId} />}
                    {editImageId && <ADetailerModal isOpen={isADetailerModalOpen} onClose={handleCloseADetailerModal} onSubmit={handleEditSubmit} imageId={editImageId} isPremium={false} />}
                    {editImageId && <FaceSwapModal isOpen={isFaceSwapModalOpen} onClose={handleCloseFaceSwapModal} onSubmit={handleEditSubmit} imageId={editImageId} />}
                    {editImageId && <BGRemovalModal isOpen={isBGRemovalModalOpen} onClose={handleCloseBGRemovalModal} onSubmit={handleEditSubmit} imageId={editImageId} />}
                    {editImage && <CanvasModal isOpen={isCanvasModalOpen} onClose={handleCloseCanvasModal} imageDto={editImage} onMaskSubmit={handleMaskSubmit} />}
                    {editImageId && editImage && <InpaintingModal isOpen={isInpaintingModalOpen} onCancel={handleCloseInpaintingModal} base64Image={base64Image!} originalImage={editImage.image} imageId={editImageId} handleInpaintClick={handleInpaintClick} onSubmit={handleEditSubmit} />}
                    {editImageId && <UpscalerModal isOpen={isUpscalerModalOpen} onClose={handleCloseUpscalerModal} imageId={editImageId} image={images.find(img => img.id === editImageId)} onSubmit={handleEditSubmit} />}
                    {selectedImage?.videoUrl && <AddAudioModal isOpen={isAudioModalOpen} onClose={handleCloseAudioModal} videoUrl={selectedImage.videoUrl} onSubmit={handleAudioSubmit} requestType={selectedImage?.imageMetadata?.requestType} />}
                    <Dialog open={audioLoading} onOpenChange={open => {
        setAudioLoading(open);
      }}>
                        <DialogContent className="sm:max-w-[425px]">
                            <DialogHeader>
                                <DialogTitle>
                                    Audio Generation in Progress
                                </DialogTitle>
                                <DialogDescription>
                                    Your request has been queued
                                </DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 py-4">
                                <div className="flex items-center gap-4">
                                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                                    <p className="text-sm text-gray-500">
                                        This process can take upto 3 minutes or
                                        longer.
                                    </p>
                                </div>
                                <p className="text-sm text-gray-500">
                                    If the request fails, you&apos;ll be
                                    refunded. Otherwise, you&apos;ll find the
                                    videos appearing in your gallery when
                                    they&apos;re created.
                                </p>
                                <p className="text-sm text-gray-500">
                                    You&apos;ll receive a notification when the
                                    process is complete.
                                </p>
                            </div>
                            <div className="flex justify-end">
                                <Button onClick={() => {
              setAudioLoading(false);
            }}>
                                    Close
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </ImageContainer>}
        </>;
};